"use client";
import React, { Suspense } from "react";
import { faCog, faEdit } from "@fortawesome/pro-solid-svg-icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import {
  AnimationList,
  BackButton,
  Button,
  IfUserIs,
  Reactions,
  ReactionSelector,
  Thread,
} from "@/components";
import { ButtonBar, Module } from "@/components/layouts";

import { ANIMATION_PAGE } from "../../../../gql/queries/animation-page";
import config from "../../../../lib/config";

import { PublicStatus } from "./public-status";
import { ReactionsBreakdown } from "./reactions-breakdown";
import { ShareButton } from "./share-button";
import { Video } from "./video";

import "./_animation-page.scss";

dayjs.extend(relativeTime);

import { useSuspenseQuery } from "@apollo/client/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Image from "next/image";
import { notFound } from "next/navigation";

import Advertisement from "@/components/advertisement";
import { ButtonGroup } from "@/components/layouts/button-group";
import { useCurrentUser } from "@/components/user-context-provider/user-context-provider";

import { BlacklistAnimationButton } from "./blacklist-button";
import { DeleteAnimationButton } from "./delete-button";
import { ReportAnimationButton } from "./report-button";

type AnimationPageInnerParams = {
  slug: string;
};

const AnimationPageInner = ({ slug }: AnimationPageInnerParams) => {
  const currentUser = useCurrentUser();

  const { data } = useSuspenseQuery(ANIMATION_PAGE, {
    variables: { slug },
  });

  if (!data.animation) notFound();

  const { animation } = data;

  const getUserActions = () => {
    return (
      <>
        <Button
          icon={faEdit}
          type="positive"
          href={`/editor/${animation.url}`}
        >
          Edit
        </Button>
        <DeleteAnimationButton animationId={animation.id} />
      </>
    );
  };

  const getAdminActions = () => {
    if (animation.blacklisted) return null;
    return (
      <>
        <BlacklistAnimationButton animationId={animation.id} />
      </>
    );
  };

  const getLength = () => {
    if (animation.sequence.length === 0) return {};
    if (animation.schemaVersion > 1) {
      return {
        frameCount: animation.frames,
        duration: animation.sequence.split("/").reduce((total, frameParts) => {
          const [delay] = frameParts.split(":");
          return total + parseInt(delay, 10);
        }, 0),
      };
    }

    try {
      const sequence = JSON.parse(animation.sequence);
      return {
        frameCount: sequence.length,
        duration: sequence.reduce((total: number, frame: string) => {
          return total + parseInt(frame[1], 10);
        }, 0),
      };
    } catch (e) {
      return {
        frameCount: animation.sequence.length,
        duration: 0,
      };
    }
  };

  const { frameCount, duration } = getLength();
  const belongsToCurrentUser = currentUser?.id === animation.userId;

  const formats = animation.format?.split(",") ?? [];
  const animationImage = formats.includes("AVIF") ? `${config.s3Path}/${animation.url}/animation.avif?v=${animation.version}` : `${config.s3Path}/${animation.url}/animation.gif?v=${animation.version}`;

  return (
    <>
      <ButtonBar>
        <ButtonGroup>
          <BackButton />
        </ButtonGroup>
        <ButtonGroup align="right">
          {currentUser?.roles?.includes("admin") ? getAdminActions() : null}
          <ShareButton animation={animation} />
          <IfUserIs userId={animation.userId}>{getUserActions()}</IfUserIs>
          <IfUserIs not userId={animation.userId}>
            <ReportAnimationButton animationId={animation.id} />
          </IfUserIs>
        </ButtonGroup>
      </ButtonBar>
      <Module>
        <IfUserIs userId={animation.userId}>
          <PublicStatus isPublic={animation.public} />
        </IfUserIs>
      </Module>
      <div className="animation-page__video-area">
        <div className="animation-page__video-container">
          {!animation.encoded ? (
            <div
              className="text-center bg-white text-gray-500 flex flex-col justify-center"
              style={{ minHeight: 400 }}
            >
              <div>
                <FontAwesomeIcon icon={faCog} size="7x" spin={true} className="text-grey-400" />
              </div>
              <p className="text-center m-4 ">Encoding...</p>
            </div>
          ) : !formats.includes('AVIF') && frameCount > 1 ? (
            <Video animation={animation} />
          ) : (
            <Image
              src={animationImage}
              alt=""
              width={animation.width}
              height={animation.height}
              style={{
                width: "100%",
                imageRendering: 'pixelated'
              }}
              unoptimized
            />
          )
          }
        </div>
      </div>
      <Module>
        <ul className="animation-page__stats">
          <li>Views: {animation.views}</li>
          <li>Created: {dayjs(animation.dateCreated).fromNow()}</li>
          <li>Last updated: {dayjs(animation.dateUpdated).fromNow()}</li>
          <li>Version: {animation.version}</li>
          <li>
            Length:{" "}
            {frameCount > 1 ? `${frameCount} frames` : `${frameCount} frame`},{" "}
            {duration / 1000}s
          </li>
        </ul>
      </Module>
      {belongsToCurrentUser ? null : (
        <Module>
          <ReactionSelector
            animationId={animation.id}
            reactionsBreakdown={animation.reactionsBreakdown}
          />
        </Module>
      )}
      <Module>
        <div className="animation-page__reactions">
          <Reactions reactions={animation.reactions} type="large" />
        </div>
      </Module>
      <Advertisement />
      <Module>
        <ReactionsBreakdown reactions={animation.reactionsBreakdown} />
      </Module>
      <Suspense fallback={<p>Loading comments...</p>}>
        <Thread thread={animation.thread} animation={animation} />
      </Suspense>
      <Module>
        {animation.olderAnimations.length > 0 ? (
          <p>More animations by {animation.user.name}:</p>
        ) : null}
        <AnimationList
          animations={animation.olderAnimations}
          showPublic={false}
        />
      </Module>
    </>
  );
};

export default AnimationPageInner;
